import { authenticatedApiClient } from './authService';


// Map frontend entity types to backend endpoint names
const endpointMap: Record<string, string> = {
    vc_firm: 'vc-firms',
    angel_investor: 'angels',
    organization: 'organizations',
    people_of_interest: 'pois',
};

// Get VC Firm details by ID
export const getVCFirmDetails = async (id: string | number) => {
    return fetchEntityDetail('vc_firm', id);
};

// Get Angel Investor details by IDs
export const getAngelInvestorDetails = async (id: string | number) => {
    return fetchEntityDetail('angel_investor', id);
};

// Get Organization details by ID
export const getOrganizationDetails = async (id: string | number) => {
    return fetchEntityDetail('organization', id);
};

// Get Person of Interest details by ID
export const getPOIDetails = async (id: string | number) => {
    return fetchEntityDetail('people_of_interest', id);
};


export const fetchEntities = async (
    entityType: 'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest',
    query: string,
    page?: number,
    filters?: Record<string, string | number>
): Promise<any> => {
    try {
        const endpoint = endpointMap[entityType];
        const params: { page?: number; search?: string; [key: string]: any } = { ...filters };
        if (page) params.page = page;
        if (query && query.trim() !== '') params.search = query;
        const response = await authenticatedApiClient.get(`/${endpoint}/`, {
            params
        });
        return response.data; // includes count, next, previous, results
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching entities:', error);
        throw error;
    }
};

// Fetch VC Firm and its related organizations (use nested field from detail response)
export const getVCFirmWithOrganizations = async (id: string | number) => {
    const vcFirm = await getVCFirmDetails(id) as { organizations?: any[] };
    return { vcFirm, organizations: vcFirm.organizations || [] };
};

// Fetch Angel and its related organizations (use nested field from detail response)
export const getAngelWithOrganizations = async (id: string | number) => {
    const angel = await getAngelInvestorDetails(id) as { organizations?: any[] };
    return { angel, organizations: angel.organizations || [] };
};

// Add type for organization detail with relations
export interface OrganizationDetailWithRelations {
    id: number;
    name: string;
    org_type?: string;
    industry?: string;
    main_hq?: string;
    has_operations_in_israel?: boolean;
    has_8200_connections?: boolean;
    description?: string;
    vc_firms?: any[];
    angels?: any[];
    [key: string]: any; // For flexibility in additional fields
}

// Fetch organization and its related VCs and angels (use backend filtering)
export const getOrganizationWithRelations = async (id: string | number) => {
    const organization = await getOrganizationDetails(id) as OrganizationDetailWithRelations;
    // Use backend filtering for related VCs and angels
    const [vcFirmsRes, angelsRes] = await Promise.all([
        fetchEntities('vc_firm', '', undefined, { 'organization__name': organization.name }),
        fetchEntities('angel_investor', '', undefined, { 'organization__name': organization.name }),
    ]);
    return {
        organization,
        vcFirms: vcFirmsRes.results || [],
        angels: angelsRes.results || [],
    };
};

// Fetch POI and its related organizations and VCs (use nested fields from detail response)
export const getPOIWithRelations = async (id: string | number) => {
    const poi = await getPOIDetails(id) as { organizations?: any[]; vc_firms?: any[] };
    return {
        poi,
        organizations: poi.organizations || [],
        vcFirms: poi.vc_firms || [],
    };
};

export const fetchEntityDetail = async (
    entityType: 'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest',
    id: string | number
): Promise<unknown> => {
    try {
        const endpoint = endpointMap[entityType];
        const response = await authenticatedApiClient.get(`/${endpoint}/${id}/`);
        return response.data;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching entity detail:', error);
        throw error;
    }
};
